import { useEffect, useRef, useState } from "react";
import { useProgress } from "@react-three/drei";
import { Toaster } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { FastForward } from "lucide-react";
import { Experience } from "../three/Experience";
import { TopBar } from "../components/TopBar";
import { ControlDeck } from "../components/ControlDeck";
import { TelemetryHUD, StreetHUD } from "../components/TelemetryHUD";
import { ScenarioDrawer } from "../components/ScenarioDrawer";
import { NarrativeGuide } from "../components/NarrativeGuide";
import { GuidedTour } from "../components/GuidedTour";
import { EventManager } from "../components/EventManager";
import { Onboarding } from "../components/Onboarding";
import { LoadingScreen } from "../components/LoadingScreen";
import { AudioEngine } from "../components/AudioEngine";
import { HapticsEngine } from "../components/HapticsEngine";
import { LightingController } from "../components/LightingController";
import { useSim } from "../lib/store";
import { loadOceanMask } from "../lib/seismic";

export default function EarthquakeStudio() {
  const { active, progress } = useProgress();
  const [maskLoaded, setMaskLoaded] = useState(false);
  const [forceReady, setForceReady] = useState(false);
  const introStartedRef = useRef(false);
  const dropMode = useSim((s) => s.dropMode);
  const viewMode = useSim((s) => s.viewMode);
  const introPlaying = useSim((s) => s.introPlaying);
  const tourActive = useSim((s) => s.tourActive);

  useEffect(() => {
    loadOceanMask().then(() => setMaskLoaded(true));
    const t = setTimeout(() => setForceReady(true), 7000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("drop-cursor", dropMode && viewMode === "macro" && !introPlaying);
  }, [dropMode, viewMode, introPlaying]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        const st = useSim.getState();
        if (st.simulating) return;
        if (st.clickStage === 1) {
          useSim.setState({ draft: null, clickStage: 0, epicenter: st.events.length ? st.epicenter : null });
        } else if (st.selectedId) {
          st.removeEvent(st.selectedId);
        } else if (st.events.length) {
          st.removeEvent(st.events[st.events.length - 1].id);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const ready = forceReady || (maskLoaded && progress >= 100 && !active);

  // Kick off the cinematic intro once, when the scene is ready.
  useEffect(() => {
    if (ready && !introStartedRef.current) {
      introStartedRef.current = true;
      useSim.getState().startIntro();
    }
  }, [ready]);

  // Safety: guarantee the intro always ends on a wall clock, independent of the render loop.
  useEffect(() => {
    if (!introPlaying) return;
    const t = setTimeout(() => useSim.getState().endIntro(), 7200);
    return () => clearTimeout(t);
  }, [introPlaying]);

  return (
    <div className="fixed inset-0 w-full h-full bg-[#030712] overflow-hidden">
      <div className="pointer-events-none absolute inset-0 z-10" style={{ background: "radial-gradient(ellipse at 50% 85%, rgba(220,38,38,0.10) 0%, rgba(3,7,18,0) 55%)" }} />

      <Experience />

      <TopBar />
      {viewMode === "street" ? <StreetHUD /> : <TelemetryHUD />}
      <ControlDeck />
      {viewMode !== "street" && <NarrativeGuide />}
      <GuidedTour />
      <EventManager />
      <Onboarding />
      <ScenarioDrawer />
      <AudioEngine />
      <HapticsEngine />
      <LightingController />

      {/* Cinematic intro — skip control */}
      <AnimatePresence>
        {introPlaying && !tourActive && viewMode === "macro" && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-x-0 bottom-10 z-40 flex flex-col items-center gap-3 pointer-events-none"
          >
            <div className="font-mono-tele text-[11px] uppercase tracking-[0.35em] text-zinc-400/90 pointer-events-none">
              Surveying tectonic boundaries & active volcanoes
            </div>
            <button
              data-testid="skip-intro-button"
              onClick={() => useSim.getState().endIntro()}
              className="pointer-events-auto flex items-center gap-2 px-4 py-2 rounded-full bg-zinc-950/80 backdrop-blur-xl border border-white/20 text-zinc-200 hover:text-white text-xs font-semibold transition-colors"
            >
              <FastForward className="h-3.5 w-3.5" /> Skip Intro
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      <LoadingScreen ready={ready} />
      <Toaster theme="dark" position="bottom-right" richColors />
    </div>
  );
}
